import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDate } from '../../utils/helpers';
import PaginatedTable from '../../components/PaginatedTable';
import TitleWithBack from '../../components/common/TitleWithBack';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useDialog } from '../../context/DialogContext';

function ExamsView() {
  const { alert, confirm } = useDialog();
  const [exams, setExams] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterExamType, setFilterExamType] = useState('');
  const [sortKey, setSortKey] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const navigate = useNavigate();

  useEffect(() => {
    fetchExams();
    fetchExamTypes();
  }, []);

  const fetchExams = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/exams', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const dataArray = Array.isArray(res.data)
        ? res.data
        : (res.data.data ? res.data.data : []);
      setExams(dataArray);
    } catch (err) {
      setError('Error fetching exams.');
    }
    setLoading(false);
  };

  const fetchExamTypes = async () => {
    try {
      const res = await axios.get('/api/exam-types');
      const dataArray = Array.isArray(res.data)
        ? res.data
        : (res.data.data ? res.data.data : []);
      setExamTypes(dataArray);
    } catch (err) {
      console.error('Error fetching exam types', err);
    }
  };

  const handleFilterChange = (e) => {
    setFilterExamType(e.target.value);
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const handleEdit = (examId) => {
    navigate(`/admin/exams/update/${examId}`);
  };

  const handleDelete = async (examId) => {
    if (!(await confirm('Are you sure you want to delete this exam?'))) return;
    try {
      await axios.delete(`/api/exams/${examId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      fetchExams();
    } catch (err) {
      alert('Error deleting exam.', { title: 'Error', variant: 'danger' });
    }
  };

  const isExamExpired = (exam) => {
    return exam.status === 'EXPIRED';
  };

  const filteredExams = exams.filter(exam => {
    const examTypeName = exam.module?.examType?.name || '';
  
    const examTypeMatch = filterExamType === '' || 
      examTypeName.toLowerCase().includes(filterExamType.toLowerCase());
  
    return examTypeMatch;
  });
  

  const sortedExams = filteredExams.sort((a, b) => {
    const valA = a[sortKey] || '';
    const valB = b[sortKey] || '';
    if (typeof valA === 'number' && typeof valB === 'number') {
      return sortOrder === 'asc' ? valA - valB : valB - valA;
    } else {
      return sortOrder === 'asc'
        ? valA.toString().localeCompare(valB.toString())
        : valB.toString().localeCompare(valA.toString());
    }
  });

  const columns = [
    { key: 'serial', header: <>#</> },
    { key: 'examType', header: <>Exam Type</> },
    { key: 'examDate', header: <>Exam Date</> },
    { key: 'moduleName', header: <>Module</> },
    { key: 'examSeries', header: <>Exam Series</> },
    { key: 'status', header: <>Status</> },
    { key: 'rescheduleReason', header: <>Reschedule Reason</> },
    { key: 'actions', header: <>Actions</> }
  ];

  const renderRow = (exam, idx, pageIndex, pageSize) => {
    const isExpired = isExamExpired(exam);
    return (
      <>
        <td>{(pageIndex * pageSize) + idx + 1}</td>
        <td>{exam.module?.examType?.name || 'N/A'}</td>
        <td>{formatDate(exam.examDate)}</td>
        <td>{exam.module?.moduleName || 'N/A'}</td>
        <td>{exam.examSeries?.name || 'N/A'}</td>
        <td>
          <span className={`badge ${isExpired ? 'bg-danger' : 
            exam.status === 'READY' ? 'bg-primary' : 
            exam.status === 'IN_PROGRESS' ? 'bg-warning' : 
            exam.status === 'COMPLETED' ? 'bg-success' : 
            exam.status === 'CANCELLED' ? 'bg-secondary' : 'bg-info'}`}>
            {exam.status}
          </span>
        </td>
        <td style={{ maxWidth: '220px', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
          {exam.rescheduleReason ? (
            <span title={exam.rescheduledAt ? new Date(exam.rescheduledAt).toLocaleString() : ''}>
              {exam.rescheduleReason}
            </span>
          ) : '-'}
        </td>
        <td>
          <FaEdit 
            style={{ 
              cursor: isExpired ? 'not-allowed' : 'pointer', 
              marginRight: '10px',
              opacity: isExpired ? 0.5 : 1
            }} 
            onClick={() => !isExpired && handleEdit(exam.id)} 
            title={isExpired ? 'Cannot edit expired exam' : 'Edit exam'}
          />
          <FaTrash 
            style={{ 
              cursor: isExpired ? 'not-allowed' : 'pointer',
              opacity: isExpired ? 0.5 : 1
            }} 
            onClick={() => !isExpired && handleDelete(exam.id)} 
            title={isExpired ? 'Cannot delete expired exam' : 'Delete exam'}
          />
        </td>
      </>
    );
  };

  return (
    <div className="container-fluid mt-2 px-4">
      <TitleWithBack title="Exams" />
      <div className="row mb-3">
        <div className="col-md-4">
          <label htmlFor="examTypeFilter" className="form-label">
            Filter by Exam Type:
          </label>
          <select
            id="examTypeFilter"
            value={filterExamType}
            onChange={handleFilterChange}
            className="form-select"
          >
            <option value="">All</option>
            {examTypes.map(et => (
              <option key={et.id} value={et.name}>{et.name}</option>
            ))}
          </select>
        </div>
      </div>
      {loading ? (
        <div>Loading exams...</div>
      ) : error ? (
        <div className="alert alert-danger" role="alert">{error}</div>
      ) : sortedExams.length === 0 ? (
        <div>Nothing to show here</div>
      ) : (
        <PaginatedTable
          data={sortedExams}
          columns={columns}
          renderRow={renderRow}
          rowsPerPage={50}
        />
      )}
    </div>
  );
}

export default ExamsView;
